import React, { useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import countriesData from '../../../assets/json/countries.json';
import { validatePhoneNumber, getPhoneMaxLength } from '../../../infrastructure/utils/validation';
import { locationService } from '../../../infrastructure/services/locationService';
import CircularProgress from '@mui/material/CircularProgress';

const countryNames = {
    '+94': 'Sri Lanka',
    '+91': 'India',
    '+1': 'United States',
    '+44': 'United Kingdom',
    '+61': 'Australia'
};

const RegisterStep2 = () => {
    const {
        formData,
        handleInputChange,
        errors,
        handleNextStep,
        autocompletePaperSx,
        setFormData,
        setErrors
    } = useOutletContext();

    const navigate = useNavigate();

    const [provincesList, setProvincesList] = React.useState([]);
    const [districtsList, setDistrictsList] = React.useState([]);
    const [loadingProvinces, setLoadingProvinces] = React.useState(false);
    const [loadingDistricts, setLoadingDistricts] = React.useState(false);

    useEffect(() => {
        const fetchProvinces = async () => {
            setLoadingProvinces(true);
            try {
                const data = await locationService.getProvinces();
                setProvincesList(data || []);
            } catch (err) {
                console.error('Failed to load provinces in register:', err);
            } finally {
                setLoadingProvinces(false);
            }
        };
        fetchProvinces();
    }, []);

    useEffect(() => {
        const activeProvince = provincesList.find(p => p.en_US === formData.district || p.name === formData.district);
        if (activeProvince) {
            const fetchDistricts = async () => {
                setLoadingDistricts(true);
                try {
                    const provId = activeProvince.index ?? activeProvince.id;
                    const data = await locationService.getDistrictsByProvince(provId);
                    setDistrictsList(data || []);
                } catch (err) {
                    console.error('Failed to load districts in register:', err);
                } finally {
                    setLoadingDistricts(false);
                }
            };
            fetchDistricts();
        } else {
            setDistrictsList([]);
        }
    }, [formData.district, provincesList]);

    // Safety check: redirect to Step 1 if personal info is empty
    useEffect(() => {
        if (!formData.fullName || !formData.dateOfBirth || !formData.gender) {
            navigate('/register/step-1', { replace: true });
        }
    }, [formData.fullName, formData.dateOfBirth, formData.gender, navigate]);

    const isPhoneValid = !validatePhoneNumber(formData.countryCode || '+94', formData.phone);

    return (
        <>
            {/* Phone Number */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', width: '100%' }}>
                    {/* Custom Country code selector with real flags */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: errors.phone ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            minWidth: '115px',
                            px: 1.5,
                            justifyContent: 'center',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        <Select
                            value={formData.countryCode || '+94'}
                            onChange={(e) => {
                                const newCode = e.target.value;
                                const maxLen = getPhoneMaxLength(newCode);
                                setFormData(prev => ({
                                    ...prev,
                                    countryCode: newCode,
                                    phone: (prev.phone || '').slice(0, maxLen)
                                }));
                                if (setErrors && errors.phone) {
                                    setErrors(prev => ({ ...prev, phone: '' }));
                                }
                            }}
                            variant="standard"
                            disableUnderline
                            IconComponent={() => (
                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: '8px' }}>
                                    <path d="M1 1L5 5L9 1" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            renderValue={(selectedCode) => {
                                const c = countriesData.find(cnt => cnt.code === selectedCode) || countriesData[0];
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            component="img"
                                            src={`https://flagcdn.com/w40/${c.iso}.png`}
                                            alt={c.label}
                                            sx={{
                                                width: '20px',
                                                height: 'auto',
                                                borderRadius: '2px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '15px', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                                            {c.code}
                                        </Typography>
                                    </Box>
                                );
                            }}
                            sx={{
                                width: '100%',
                                height: '100%',
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '15px',
                                color: '#334155',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    paddingLeft: '0px !important',
                                    paddingRight: '20px !important',
                                    background: 'none',
                                    '&:focus': {
                                        background: 'none'
                                    }
                                }
                            }}
                        >
                            {countriesData.map((c) => (
                                <MenuItem
                                    key={c.code}
                                    value={c.code}
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`https://flagcdn.com/w40/${c.iso}.png`}
                                        alt={c.label}
                                        sx={{
                                            width: '20px',
                                            height: 'auto',
                                            borderRadius: '2px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                                        {countryNames[c.code] || c.label} ({c.code})
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {/* Local Mobile Number Input Box */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: errors.phone ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                                <line x1="12" y1="18" x2="12.01" y2="18" />
                            </svg>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        <Box
                            component="input"
                            type="text"
                            name="phone"
                            placeholder="Mobile Number"
                            maxLength={getPhoneMaxLength(formData.countryCode || '+94')}
                            value={formData.phone}
                            onChange={handleInputChange}
                            sx={{
                                flex: 1,
                                padding: '16px',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '15px',
                                color: '#334155',
                                outline: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                width: '100%'
                            }}
                        />
                    </Box>
                </Box>
                {errors.phone && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', px: 1, fontFamily: "'Poppins', sans-serif" }}>
                        {errors.phone}
                    </Typography>
                )}
            </Box>

            {/* Email Address */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1.5px solid',
                        borderColor: errors.email ? '#e74c3c' : '#cbd5e1',
                        borderRadius: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.25s ease',
                        '&:focus-within': {
                            borderColor: '#ff502d',
                            boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                        }
                    }}
                >
                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                    </Box>
                    <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <Box
                        component="input"
                        type="email"
                        name="email"
                        placeholder="Email Address (Optional)"
                        value={formData.email}
                        onChange={handleInputChange}
                        sx={{
                            flex: 1,
                            padding: '16px',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '15px',
                            color: '#334155',
                            outline: 'none',
                            fontFamily: "'Poppins', sans-serif",
                            width: '100%'
                        }}
                    />
                </Box>
                {errors.email && (
                    <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                        {errors.email}
                    </Typography>
                )}
            </Box>

            {/* Province & District Row */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                {/* Province */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: errors.district ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        <Autocomplete
                            options={provincesList}
                            getOptionLabel={(option) => option.en_US && option.si_LK ? `${option.en_US} (${option.si_LK})` : option.en_US || option.name || ''}
                            isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                            value={provincesList.find(p => p.en_US === formData.district || p.name === formData.district) || null}
                            onChange={(_, newVal) => {
                                setFormData(prev => ({
                                    ...prev,
                                    district: newVal ? (newVal.en_US || newVal.name) : '',
                                    city: ''
                                }));
                                setErrors(prev => ({
                                    ...prev,
                                    district: '',
                                    city: ''
                                }));
                            }}
                            loading={loadingProvinces}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    paddingLeft: '8px',
                                    '& fieldset': {
                                        border: 'none'
                                    },
                                    '&:hover fieldset': {
                                        border: 'none'
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none'
                                    },
                                    '& input': {
                                        color: '#334155',
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '15px',
                                        padding: '16px 14px 16px 4px'
                                    }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Province"
                                    InputProps={{
                                        ...(params.InputProps || {}),
                                        endAdornment: (
                                            <>
                                                {loadingProvinces ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps?.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            slotProps={{
                                paper: {
                                    sx: autocompletePaperSx
                                }
                            }}
                        />
                    </Box>
                    {errors.district && (
                        <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                            {errors.district}
                        </Typography>
                    )}
                </Box>

                {/* District */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: errors.city ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: formData.district ? '#ffffff' : '#f8fafc',
                            transition: 'all 0.25s ease',
                            opacity: formData.district ? 1 : 0.7,
                            '&:focus-within': {
                                borderColor: formData.district ? '#ff502d' : '#cbd5e1',
                                boxShadow: formData.district ? '0 0 0 3px rgba(255, 80, 45, 0.1)' : 'none'
                            }
                        }}
                    >
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        <Autocomplete
                            options={districtsList}
                            getOptionLabel={(option) => option.en_US && option.si_LK ? `${option.en_US} (${option.si_LK})` : option.en_US || option.name || ''}
                            isOptionEqualToValue={(option, val) => (option.index ?? option.id) === (val.index ?? val.id)}
                            value={districtsList.find(d => d.en_US === formData.city || d.name === formData.city) || null}
                            onChange={(_, newVal) => {
                                setFormData(prev => ({
                                    ...prev,
                                    city: newVal ? (newVal.en_US || newVal.name) : ''
                                }));
                                setErrors(prev => ({
                                    ...prev,
                                    city: ''
                                }));
                            }}
                            disabled={!formData.district}
                            loading={loadingDistricts}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent',
                                    paddingLeft: '8px',
                                    '& fieldset': {
                                        border: 'none'
                                    },
                                    '&:hover fieldset': {
                                        border: 'none'
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none'
                                    },
                                    '& input': {
                                        color: '#334155',
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '15px',
                                        padding: '16px 14px 16px 4px',
                                        cursor: !formData.district ? 'not-allowed' : 'pointer'
                                    }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={formData.district ? (loadingDistricts ? "Loading districts..." : "Select District") : "Select Province First"}
                                    InputProps={{
                                        ...(params.InputProps || {}),
                                        endAdornment: (
                                            <>
                                                {loadingDistricts ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps?.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            slotProps={{
                                paper: {
                                    sx: autocompletePaperSx
                                }
                            }}
                        />
                    </Box>
                    {errors.city && (
                        <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                            {errors.city}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Navigation Buttons for Step 2 */}
            <Box sx={{ display: 'flex', gap: 2, mt: '10px' }}>
                <Button
                    type="button"
                    onClick={() => navigate('/register/step-1')}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderColor: '#ff502d',
                        color: '#ff502d',
                        borderRadius: '10px',
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontFamily: "'Poppins', sans-serif",
                        '&:hover': {
                            borderColor: '#e04324',
                            color: '#e04324',
                            background: 'rgba(255, 80, 45, 0.05)'
                        }
                    }}
                >
                    Back
                </Button>
                <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!isPhoneValid || !formData.district || !formData.city}
                    variant="contained"
                    sx={{
                        flex: 1,
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 3px 8px rgba(255, 80, 45, 0.15)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-1.5px)',
                            boxShadow: '0 5px 12px rgba(255, 80, 45, 0.25)'
                        },
                        '&:disabled': {
                            opacity: 0.6,
                            backgroundColor: '#ff502d',
                            color: '#ffffff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    Next Step
                </Button>
            </Box>
        </>
    );
};

export default RegisterStep2;
